import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  Alert,
  AlertTitle,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  Snackbar,
} from '@mui/material';
import {
  Edit as EditIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
} from '@mui/icons-material';
import { schoolsApi } from '../../utils/apiService';

const GRADE_ORDER = ['KG', '01', '02', '03', '04', '05', '06', '07', '08', '09', '10', '11', '12'];

const GradeCapacityManager = ({ selectedSchool, onCapacityChange, userPermissions }) => {
  const [capacityData, setCapacityData] = useState({});
  const [gradeDisplayMap, setGradeDisplayMap] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [editingData, setEditingData] = useState({});
  const [saveLoading, setSaveLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  // Load capacity data
  const loadData = useCallback(async () => {
    if (!selectedSchool?.uuid) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const data = await schoolsApi.getGradeCapacities(selectedSchool.uuid);
      
      setCapacityData(data.capacities || {});
      setGradeDisplayMap(data.grade_display_map || {});
      
    } catch (error) {
      console.error('Error loading grade capacity data:', error);
      setError(error.message || 'Failed to load grade capacity data');
    } finally {
      setLoading(false);
    }
  }, [selectedSchool?.uuid]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Start editing mode
  const handleEditMode = () => {
    setEditMode(true);
    setEditingData(JSON.parse(JSON.stringify(capacityData)));
  };

  // Cancel editing
  const handleCancelEdit = () => {
    setEditMode(false);
    setEditingData({});
  };

  // Update capacity value during editing
  const handleCapacityChange = (grade, value) => {
    const numValue = value === '' ? '' : parseInt(value) || '';
    
    setEditingData(prev => ({
      ...prev,
      [grade]: numValue
    }));
  };

  // Save capacity data
  const handleSave = async () => {
    setSaveLoading(true);
    
    try {
      // Prepare data for API - include all grades that have been edited
      // Send null for cleared capacities so backend can deactivate them
      const dataToSave = {};
      
      // Include all edited grades
      Object.entries(editingData).forEach(([grade, capacity]) => {
        if (capacity === '' || capacity === null || capacity === undefined) {
          // If the grade previously had a capacity, send null to deactivate it
          if (capacityData[grade]) {
            dataToSave[grade] = null;
          }
        } else {
          dataToSave[grade] = capacity;
        }
      });

      await schoolsApi.saveGradeCapacities(selectedSchool.uuid, dataToSave);

      // Success - reload data and exit edit mode
      await loadData();
      setEditMode(false);
      setEditingData({});
      setSuccessMessage('Grade capacities saved successfully!');
      
      // Notify parent component of changes and wait for completion (triggers projection recalculation)
      console.log('[GradeCapacityManager] Calling onCapacityChange callback...');
      if (onCapacityChange) {
        await onCapacityChange();
        console.log('[GradeCapacityManager] onCapacityChange callback completed');
      } else {
        console.warn('[GradeCapacityManager] No onCapacityChange callback provided!');
      }

    } catch (error) {
      console.error('Error saving grade capacity data:', error);
      setError(error.message || 'Failed to save grade capacity data');
    } finally {
      setSaveLoading(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error">
        <AlertTitle>Error Loading Grade Capacity Data</AlertTitle>
        {error}
      </Alert>
    );
  }

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Sticky Header Controls */}
      <Box 
        sx={{ 
          position: 'sticky',
          top: 0,
          backgroundColor: 'background.paper',
          zIndex: 10,
          p: 3,
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      >
          <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
            <Box sx={{ flex: 1 }}>
              <Typography variant="h6" component="h3">
                📏 Maximum Grade Capacity
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Set the maximum capacity for each grade level. These limits will be applied to all projection years.
              </Typography>
            </Box>
            
            {userPermissions.canEdit && (
              <Box display="flex" gap={1} sx={{ flexShrink: 0, ml: 2 }}>
                {editMode ? (
                  <>
                    <Button
                      variant="outlined"
                      onClick={handleCancelEdit}
                      startIcon={<CancelIcon />}
                    >
                      Cancel
                    </Button>
                    <Button
                      variant="contained"
                      onClick={handleSave}
                      startIcon={<SaveIcon />}
                      disabled={saveLoading}
                    >
                      {saveLoading ? <CircularProgress size={20} /> : 'Save'}
                    </Button>
                  </>
                ) : (
                  <Button
                    variant="contained"
                    onClick={handleEditMode}
                    startIcon={<EditIcon />}
                  >
                    Edit Capacities
                  </Button>
                )}
              </Box>
            )}
          </Box>

          {/* School Information */}
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            School: {selectedSchool?.name || 'Unknown School'}
          </Typography>

          {/* Edit Mode Help Text */}
          {editMode && (
            <Alert severity="info" sx={{ mt: 2 }}>
              Enter the maximum number of students that can be enrolled in each grade. 
              Leave blank or set to 0 to remove a capacity constraint for that grade.
            </Alert>
          )}
        </Box>

      {/* Scrollable Content Area */}
      <Box sx={{ flex: 1, overflow: 'auto', p: 3, pt: 2 }}>
        {/* Capacity Table */}
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell><strong>Grade</strong></TableCell>
                <TableCell align="center"><strong>Max Capacity</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {GRADE_ORDER.map(grade => {
                const currentData = editMode ? editingData : capacityData;
                const value = currentData[grade] || '';
                const hasCapacity = value !== '' && value !== 0;
                
                return (
                  <TableRow key={grade}>
                    <TableCell>
                      <Typography variant="body2" fontWeight="medium">
                        {gradeDisplayMap[grade] || grade}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      {editMode ? (
                        <TextField
                          size="small"
                          type="number"
                          value={value}
                          onChange={(e) => handleCapacityChange(grade, e.target.value)}
                          inputProps={{ 
                            min: 0, 
                            max: 999, 
                            style: { textAlign: 'center' } 
                          }}
                          sx={{ width: 120 }}
                          placeholder="--"
                        />
                      ) : (
                        <Typography 
                          variant="body2"
                          color={hasCapacity ? 'text.primary' : 'text.disabled'}
                        >
                          {hasCapacity ? value : '--'}
                        </Typography>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Status Message */}
        {!editMode && Object.keys(capacityData).length === 0 && (
          <Box mt={2}>
            <Alert severity="info">
              No grade capacities have been set. Click "Edit Capacities" to add capacity constraints for your projections.
            </Alert>
          </Box>
        )}
      </Box>

      {/* Success Snackbar */}
      <Snackbar
        open={!!successMessage}
        autoHideDuration={6000}
        onClose={() => setSuccessMessage('')}
        message={successMessage}
      />
    </Box>
  );
};

export default GradeCapacityManager;


